import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomTable from '../../common/CustomTable';
import SideDrawer from '../../common/SideDrawer';
import DialogBox from '../../common/DialogBox';
import Button from '../../common/Button';
import CreateTenantSideDrawerDialog from './CreateTenantSideDrawerDialog';
import TenantUserListDialog from './TenantUserListDialog';
import ResetTenantDataDialog from './ResetTenantDataDialog';
import apiService from '../../../services/ApiService';
import { apiRoutes } from '../../../shared/apiRoutes';
import { Tenant, ApiTenant } from '../../../shared/interfaces';
import { PencilIcon, TrashIcon, KeyIcon, UsersIcon } from '@heroicons/react/20/solid';
import { PowerIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { useAuthorization } from '../../../auth/useAuthorization';
import { Role } from '../../../auth/roles';
import { formatDateOnly } from '../../../utils/util';

// Paginated API response structure
interface PaginatedResponse {
  data?: ApiTenant[];
  items?: ApiTenant[];
  totalCount?: number;
  total?: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

interface TenantStatusResponse {
  success: boolean;
  message: string;
  tenantId: number;
  isActive: boolean;
  usersUpdated?: number;
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error) return 'Request failed';
  if (typeof error === 'string') return error;
  const err = error as { message?: string; Message?: string; error?: string };
  return err.message ?? err.Message ?? err.error ?? 'Request failed';
};

const Tenants: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, isAuthenticated } = useAuthorization();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [resetTenant, setResetTenant] = useState<Tenant | null>(null);
  const [resetPasswordTenant, setResetPasswordTenant] = useState<Tenant | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [userListTenantId, setUserListTenantId] = useState<number | null>(null);
  const [userListOpen, setUserListOpen] = useState(false);
  const [statusDialogTenant, setStatusDialogTenant] = useState<Tenant | null>(null);
  const [statusTargetActive, setStatusTargetActive] = useState<boolean>(true);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortColumn, setSortColumn] = useState<keyof Tenant | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Authorization check: Only Admin users can access this page
  useEffect(() => {
    // Wait for authentication to be determined
    if (isAuthenticated === false) {
      // Not authenticated, redirect to login
      navigate('/login', { replace: true });
      return;
    }

    // Check if user has Admin role
    if (isAuthenticated && !hasRole(Role.ADMIN)) {
      // User is authenticated but not Admin - redirect and show error
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: 'You do not have permission to access this page. Only Admin users can manage tenants.',
          type: 'error'
        }
      }));
      navigate('/game/oneday#home', { replace: true });
      return;
    }
  }, [isAuthenticated, hasRole, navigate]);

  useEffect(() => {
    // Only fetch tenants if user is Admin
    if (isAuthenticated && hasRole(Role.ADMIN)) {
      fetchTenants();
    }
    
    // Listen for tenant created/updated event to refresh the list
    const handleTenantChanged = () => {
      fetchTenants();
    };
    
    window.addEventListener('tenant-changed', handleTenantChanged);
    
    return () => {
      window.removeEventListener('tenant-changed', handleTenantChanged);
    };
  }, []);

  const mapApiTenantToTenant = (apiTenant: ApiTenant): Tenant => {
    // Helper function to convert MySQL TINYINT(1) or number to boolean
    const toBoolean = (value: any, defaultValue: boolean = false): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      if (value === '1' || value === 'true' || value === true) return true;
      if (value === '0' || value === 'false' || value === false) return false;
      return defaultValue;
    };

    // Handle modifiedBy - API returns number (user ID) or null, we want to display name if available
    const getModifiedBy = (): string => {
      const modifiedByName = apiTenant.modifiedByName || apiTenant.modified_by_name;
      
      // If modifiedByName is null/undefined/empty, display tenantName instead
      if (!modifiedByName || modifiedByName === null || modifiedByName === '') {
        return apiTenant.tenantName || apiTenant.tenant_name || apiTenant.name || '';
      }
      
      // Prefer modifiedByName (user name) if available
      return modifiedByName;
    };

    return {
      id: apiTenant.id ?? apiTenant.tenantId ?? apiTenant.tenant_id ?? 0,
      tenantName: apiTenant.tenantName || apiTenant.tenant_name || apiTenant.name || '',
      role: apiTenant.role || apiTenant.Role || '',
      databaseName: apiTenant.databaseName || apiTenant.database_name || apiTenant.dbName || '',
      mobileNo: apiTenant.mobileNo || apiTenant.mobile_no || apiTenant.mobile || apiTenant.phoneNumber || apiTenant.phone_number || apiTenant.phone || '',
      remarks: apiTenant.remarks || apiTenant.remark || apiTenant.notes || '',
      createdOn: apiTenant.createdOn || apiTenant.created_on || apiTenant.createdAt || apiTenant.created_at || '',
      modifiedBy: getModifiedBy(),
      modifiedAt: apiTenant.modifiedOn || apiTenant.modified_on || apiTenant.modifiedAt || apiTenant.modified_at || apiTenant.updatedAt || new Date().toISOString(),
      isActive: toBoolean(apiTenant.isActive ?? apiTenant.is_active ?? apiTenant.active, true),
      isDeleted: toBoolean(apiTenant.isDeleted ?? apiTenant.is_deleted ?? apiTenant.deleted, false),
      dataResetOn: apiTenant.dataResetOn || apiTenant.data_reset_on || apiTenant.dataResetDate || apiTenant.data_reset_date || apiTenant.resetDate || apiTenant.reset_date || null,
    };
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      // API returns a direct array, not wrapped in pagination object
      const response = await apiService.get<ApiTenant[]>(apiRoutes.tenants.list);
      
      // Handle response - API returns direct array
      let tenantsData: ApiTenant[] = [];
      
      if (Array.isArray(response)) {
        tenantsData = response;
      } else {
        // Fallback: try to extract array from response object
        const responseObj = response as any;
        if (responseObj.data && Array.isArray(responseObj.data)) {
          tenantsData = responseObj.data;
        } else if (responseObj.items && Array.isArray(responseObj.items)) {
          tenantsData = responseObj.items;
        } else {
          console.warn('Unexpected response format:', response);
          tenantsData = [];
        }
      }

      // Map API response to Tenant interface
      const mappedTenants = tenantsData.map(mapApiTenantToTenant);
      setTenants(mappedTenants);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Failed to load tenants', type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  const displayedTenants = useMemo(() => {
    const textQuery = (key: string) => (filters[key] || '').trim().toLowerCase();
    const includes = (value: string | null | undefined, query: string) =>
      !query || (value || '').toLowerCase().includes(query);

    const matchesDate = (value: string | null | undefined, query: string) => {
      if (!query) return true;
      if (!value) return false;
      const shown = formatDateOnly(value);
      const [year, month, day] = query.split('-');
      const picked = year && month && day ? `${day}/${month}/${year}` : query;
      return shown === picked || shown.includes(query) || value.slice(0, 10) === query;
    };

    const selectedRoles = (filters.role || '')
      .split('|')
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);

    let rows = tenants.filter((tenant) => {
      if (!includes(tenant.tenantName, textQuery('tenantName'))) return false;
      if (selectedRoles.length > 0) {
        const tenantRoles = (tenant.role || '')
          .split(',')
          .map((role) => role.trim().toLowerCase())
          .filter(Boolean);
        if (!selectedRoles.some((role) => tenantRoles.includes(role))) return false;
      }
      if (!includes(tenant.databaseName, textQuery('databaseName'))) return false;
      if (!includes(tenant.mobileNo, textQuery('mobileNo'))) return false;
      if (!includes(tenant.remarks, textQuery('remarks'))) return false;
      if (!matchesDate(tenant.createdOn, filters.createdOn || '')) return false;
      if (filters.isActive === 'active' && !tenant.isActive) return false;
      if (filters.isActive === 'inactive' && tenant.isActive) return false;
      if (!matchesDate(tenant.dataResetOn, filters.dataResetOn || '')) return false;
      if (!includes(tenant.modifiedBy, textQuery('modifiedBy'))) return false;
      if (!matchesDate(tenant.modifiedAt, filters.modifiedAt || '')) return false;
      return true;
    });

    if (sortColumn && sortColumn !== 'id') {
      const direction = sortDirection === 'asc' ? 1 : -1;
      const dateColumns = new Set<keyof Tenant>(['createdOn', 'modifiedAt', 'dataResetOn']);
      rows = [...rows].sort((left, right) => {
        if (sortColumn === 'isActive') {
          const leftLabel = left.isActive ? 'Active' : 'Inactive';
          const rightLabel = right.isActive ? 'Active' : 'Inactive';
          return leftLabel.localeCompare(rightLabel) * direction;
        }
        if (dateColumns.has(sortColumn)) {
          const leftTime = Date.parse(String(left[sortColumn] || ''));
          const rightTime = Date.parse(String(right[sortColumn] || ''));
          const leftValue = Number.isNaN(leftTime) ? null : leftTime;
          const rightValue = Number.isNaN(rightTime) ? null : rightTime;
          if (leftValue == null && rightValue == null) return 0;
          if (leftValue == null) return 1;
          if (rightValue == null) return -1;
          return (leftValue - rightValue) * direction;
        }
        return String(left[sortColumn] ?? '').localeCompare(String(right[sortColumn] ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * direction;
      });
    }

    return rows;
  }, [tenants, filters, sortColumn, sortDirection]);

  useEffect(() => {
    setTotalPages(Math.ceil(displayedTenants.length / rowsPerPage));
    setCurrentPage(0);
  }, [displayedTenants, rowsPerPage]);

  const roleFilterOptions = useMemo(() => {
    const names = new Set<string>();
    tenants.forEach((tenant) => {
      (tenant.role || '').split(',').forEach((part) => {
        const name = part.trim();
        if (name && name !== '—') names.add(name);
      });
    });
    return Array.from(names)
      .sort((left, right) => left.localeCompare(right))
      .map((name) => ({ value: name, label: name }));
  }, [tenants]);

  const handleFilter = (appliedFilters: Record<string, string>) => {
    setFilters(appliedFilters);
  };

  const handleSort = (column: keyof Tenant) => {
    if (column === 'id') return;
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const handleDelete = (id: number) => {
    setStatusDialogTenant(null);
    setDeleteId(id);
    setAdminPassword('');
    setAdminPasswordError('');
  };

  const closeDeleteDialog = useCallback(() => {
    if (submittingStatus) return;
    setDeleteId(null);
    setAdminPassword('');
    setAdminPasswordError('');
  }, [submittingStatus]);

  const deleteTenantWithPassword = async (id: number, password: string) => {
    const body = { adminPassword: password, password };
    try {
      await apiService.delete(apiRoutes.tenants.delete(id), body);
      return;
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string; error?: string };
      if (err.status === 401 || err.status === 403) throw error;
      const msg = extractApiErrorMessage(error);
      if (/invalid admin password|incorrect password|wrong password/i.test(msg)) throw error;
      try {
        await apiService.post(apiRoutes.tenants.deleteConfirm(id), body);
      } catch (postError: unknown) {
        const postErr = postError as { status?: number };
        if (postErr.status === 401 || postErr.status === 403) throw postError;
        await apiService.delete(
          `${apiRoutes.tenants.delete(id)}?adminPassword=${encodeURIComponent(password)}`,
        );
      }
    }
  };

  const confirmTenantDelete = useCallback(async () => {
    if (!deleteId) return;
    const trimmedPassword = adminPassword.trim();
    if (!trimmedPassword) {
      setAdminPasswordError('Your login password is required.');
      return;
    }

    setSubmittingStatus(true);
    setAdminPasswordError('');
    try {
      await deleteTenantWithPassword(deleteId, trimmedPassword);
      setTenants((current) => current.filter((tenant) => tenant.id !== deleteId));
      closeDeleteDialog();
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Tenant deleted successfully', type: 'success' },
      }));
    } catch (error: unknown) {
      const err = error as { status?: number };
      const msg = extractApiErrorMessage(error);
      if (err.status === 401 || /invalid admin password|incorrect password|wrong password/i.test(msg)) {
        setAdminPasswordError('Incorrect password');
        return;
      }
      if (err.status === 403) {
        setAdminPasswordError('You do not have permission to delete tenants.');
        return;
      }
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: msg || 'Failed to delete tenant', type: 'error' },
      }));
    } finally {
      setSubmittingStatus(false);
    }
  }, [deleteId, adminPassword, closeDeleteDialog]);

  const handleActivateDeactivate = (tenant: Tenant) => {
    setDeleteId(null);
    setStatusDialogTenant(tenant);
    setStatusTargetActive(!tenant.isActive);
    setAdminPassword('');
    setAdminPasswordError('');
  };

  const closeStatusDialog = useCallback(() => {
    if (submittingStatus) return;
    setStatusDialogTenant(null);
    setAdminPassword('');
    setAdminPasswordError('');
  }, [submittingStatus]);

  const confirmTenantStatusChange = useCallback(async () => {
    if (!statusDialogTenant) return;
    const trimmedPassword = adminPassword.trim();
    if (!trimmedPassword) {
      setAdminPasswordError('Admin password is required.');
      return;
    }

    setSubmittingStatus(true);
    setAdminPasswordError('');
    try {
      const result = await apiService.put<TenantStatusResponse>(
        apiRoutes.tenants.setStatus(statusDialogTenant.id),
        { isActive: statusTargetActive, adminPassword: trimmedPassword },
      );
      const data = (result as TenantStatusResponse)?.message ? (result as TenantStatusResponse) : (result as { data?: TenantStatusResponse }).data ?? result;
      const message =
        (data as TenantStatusResponse)?.message ??
        `Tenant ${statusTargetActive ? 'activated' : 'deactivated'} successfully`;
      const usersUpdated = (data as TenantStatusResponse)?.usersUpdated;

      await fetchTenants();
      closeStatusDialog();

      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: usersUpdated != null ? `${message} (${usersUpdated} user(s) updated)` : message,
          type: 'success',
        },
      }));
    } catch (error: unknown) {
      const msg = extractApiErrorMessage(error);
      if (/already active|already inactive/i.test(msg)) {
        await fetchTenants();
        closeStatusDialog();
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: msg, type: 'info' },
        }));
        return;
      }
      if (/invalid admin password|incorrect password/i.test(msg)) {
        setAdminPasswordError('Incorrect password');
        return;
      }
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: msg, type: 'error' },
      }));
    } finally {
      setSubmittingStatus(false);
    }
  }, [statusDialogTenant, statusTargetActive, adminPassword, closeStatusDialog]);

  const handleResetData = (tenant: Tenant) => {
    setDeleteId(null);
    setStatusDialogTenant(null);
    setResetTenant(tenant);
  };

  const closeResetPasswordDialog = () => {
    if (resettingPassword) return;
    setResetPasswordTenant(null);
    setNewPassword('');
    setConfirmNewPassword('');
    setResetPasswordError('');
    setAdminPassword('');
  };

  const handleResetPassword = (tenant: Tenant) => {
    setDeleteId(null);
    setStatusDialogTenant(null);
    setResetPasswordTenant(tenant);
    setNewPassword('');
    setConfirmNewPassword('');
    setResetPasswordError('');
    setAdminPassword('');
  };

  const confirmResetTenantPassword = async () => {
    if (!resetPasswordTenant || resettingPassword) return;
    const nextPassword = newPassword.trim();
    if (nextPassword.length < 6) {
      setResetPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (nextPassword !== confirmNewPassword.trim()) {
      setResetPasswordError('Passwords do not match.');
      return;
    }
    if (!adminPassword.trim()) {
      setResetPasswordError('Your admin password is required.');
      return;
    }

    setResettingPassword(true);
    setResetPasswordError('');
    try {
      const response = await apiService.post<{ message?: string }>(
        apiRoutes.tenants.resetPassword(resetPasswordTenant.id),
        { newPassword: nextPassword, adminPassword: adminPassword.trim() },
      );
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: response?.message || 'Tenant user password reset successfully', type: 'success' },
      }));
      setResetPasswordTenant(null);
      setNewPassword('');
      setConfirmNewPassword('');
      setAdminPassword('');
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string; error?: string };
      const msg = extractApiErrorMessage(error);
      if (err.status === 401 || /invalid password|incorrect password/i.test(msg)) {
        setResetPasswordError('Incorrect admin password');
      } else {
        setResetPasswordError(msg || 'Failed to reset the tenant user password');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  const handleUserList = (id: number) => {
    setUserListTenantId(id);
    setUserListOpen(true);
  };

  const handleCreate = () => {
    setEditId(null);
    setSideDrawerOpen(true);
  };

  const handleEditClick = (id: number) => {
    setEditId(id);
    setSideDrawerOpen(true);
  };

  // Don't render anything if user is not authenticated or not Admin
  // (redirect will happen via useEffect)
  if (!isAuthenticated || !hasRole(Role.ADMIN)) {
    return null;
  }

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden mb-4">
      <CustomTable<Tenant>
        variant="gameList"
        data={displayedTenants.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)}
        title="Tenants"
        columns={[
          { key: 'tenantName', label: 'Tenant Name', sortable: true, filterable: true },
          {
            key: 'role',
            label: 'Role',
            sortable: true,
            filterable: true,
            filterType: 'multiselect',
            filterOptions: roleFilterOptions,
            render: (item: Tenant) => item.role || '—',
          },
          { key: 'databaseName', label: 'Database Name', sortable: true, filterable: true },
          { key: 'mobileNo', label: 'Mobile No', sortable: true, filterable: true },
          { key: 'remarks', label: 'Remarks', sortable: true, filterable: true },
          {
            key: 'createdOn',
            label: 'Created On',
            sortable: true,
            filterable: true,
            filterType: 'date',
            render: (item: Tenant) => (item.createdOn ? (formatDateOnly(item.createdOn) || '-') : '-'),
          },
          {
            key: 'isActive',
            label: 'Status',
            sortable: true,
            filterable: true,
            filterType: 'select',
            filterOptions: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
            render: (item: Tenant) => (
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                  item.isActive
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-200 text-slate-600'
                }`}
              >
                {item.isActive ? 'Active' : 'Inactive'}
              </span>
            ),
          },
          {
            key: 'dataResetOn',
            label: 'Data Reset On',
            sortable: true,
            filterable: true,
            filterType: 'date',
            render: (item: Tenant) => item.dataResetOn ? (formatDateOnly(item.dataResetOn) || '-') : '-',
          },
          {
            key: 'modifiedBy',
            label: 'Modified By',
            sortable: true,
            filterable: true,
          },
          {
            key: 'modifiedAt',
            label: 'Modified At',
            sortable: true,
            filterable: true,
            filterType: 'date',
            render: (item: Tenant) => formatDateOnly(item.modifiedAt) || '—',
          },
          {
            key: 'id' as keyof Tenant, // Using id as key for actions column
            label: 'Actions',
            render: (item: Tenant) => (
              <div className="flex items-center justify-end space-x-2">
                <PencilIcon
                  className="w-5 h-5 text-blue-600 cursor-pointer hover:text-blue-800"
                  onClick={() => handleEditClick(item.id)}
                  title="Edit"
                />
                <TrashIcon
                  className="w-5 h-5 text-red-600 cursor-pointer hover:text-red-800"
                  onClick={() => handleDelete(item.id)}
                  title="Delete"
                />
                <PowerIcon
                  className={`w-5 h-5 cursor-pointer hover:opacity-80 ${
                    item.isActive ? 'text-amber-600 hover:text-amber-800' : 'text-green-600 hover:text-green-800'
                  }`}
                  onClick={() => handleActivateDeactivate(item)}
                  title={item.isActive ? 'Deactivate' : 'Activate'}
                />
                <ArrowPathIcon
                  className="w-5 h-5 text-orange-600 cursor-pointer hover:text-orange-800"
                  onClick={() => handleResetData(item)}
                  title="Reset Data"
                />
                <KeyIcon
                  className="w-5 h-5 text-purple-600 cursor-pointer hover:text-purple-800"
                  onClick={() => handleResetPassword(item)}
                  title="Reset Password"
                />
                <UsersIcon
                  className="w-5 h-5 text-indigo-600 cursor-pointer hover:text-indigo-800"
                  onClick={() => handleUserList(item.id)}
                  title="User List"
                />
              </div>
            ),
          },
        ]}
        headerButtons={[
          { label: 'Create New', onClick: handleCreate },
        ]}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        loading={loading}
        onFilter={handleFilter}
        onSort={handleSort}
        sortColumn={sortColumn ?? undefined}
        sortDirection={sortDirection}
        getId={(tenant: Tenant) => tenant.id}
      />
      </div>

      {/* Create/Edit Right Drawer */}
      <SideDrawer
        isOpen={sideDrawerOpen}
        onClose={() => {
          setSideDrawerOpen(false);
          setEditId(null);
        }}
        title={editId ? "Edit Tenant" : "Create Tenant"}
        width="w-[460px]"
      >
        <CreateTenantSideDrawerDialog
          isOpen={sideDrawerOpen}
          editId={editId}
          onClose={() => {
            setSideDrawerOpen(false);
            setEditId(null);
          }}
          onSuccess={() => {
            // Refresh the list after successful save
            fetchTenants();
          }}
        />
      </SideDrawer> 

      <DialogBox
        isOpen={resetPasswordTenant !== null}
        onClose={closeResetPasswordDialog}
        title="Reset tenant user password"
        panelClassName="w-[min(100vw-2rem,28rem)]"
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-700">
            Set a new password for the login user of{' '}
            <span className="font-semibold">{resetPasswordTenant?.tenantName}</span>.
            Their current session will end.
          </p>
          <div>
            <label htmlFor="tenant-login-new-password" className="block text-sm font-medium text-slate-700 mb-1">
              New password
            </label>
            <input
              id="tenant-login-new-password"
              type="password"
              value={newPassword}
              autoFocus
              autoComplete="new-password"
              disabled={resettingPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setResetPasswordError('');
              }}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="tenant-login-confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
              Confirm new password
            </label>
            <input
              id="tenant-login-confirm-password"
              type="password"
              value={confirmNewPassword}
              autoComplete="new-password"
              disabled={resettingPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
                setResetPasswordError('');
              }}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="tenant-login-admin-password" className="block text-sm font-medium text-slate-700 mb-1">
              Your admin password
            </label>
            <input
              id="tenant-login-admin-password"
              type="password"
              value={adminPassword}
              autoComplete="current-password"
              disabled={resettingPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setResetPasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void confirmResetTenantPassword();
                }
              }}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {resetPasswordError ? <p className="text-xs text-red-600">{resetPasswordError}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={closeResetPasswordDialog}
              disabled={resettingPassword}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmResetTenantPassword()}
              disabled={resettingPassword}
              className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-md"
            >
              {resettingPassword ? 'Saving…' : 'Reset password'}
            </Button>
          </div>
        </div>
      </DialogBox>

      <DialogBox
        isOpen={deleteId !== null}
        onClose={closeDeleteDialog}
        title="Delete Tenant"
        panelClassName="w-[min(100vw-2rem,28rem)]"
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-700">
            Delete this tenant and its database? The database cannot be restored. Enter your login password to confirm.
          </p>
          <div>
            <label htmlFor="tenant-delete-admin-password" className="block text-sm font-medium text-slate-700 mb-1">
              Your password
            </label>
            <input
              id="tenant-delete-admin-password"
              type="password"
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setAdminPasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void confirmTenantDelete();
                }
              }}
              autoFocus
              autoComplete="current-password"
              disabled={submittingStatus}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your login password"
            />
            {adminPasswordError ? (
              <p className="text-xs text-red-600 mt-1">{adminPasswordError}</p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={closeDeleteDialog}
              disabled={submittingStatus}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmTenantDelete()}
              disabled={submittingStatus}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
            >
              {submittingStatus ? 'Deleting…' : 'Delete Tenant'}
            </Button>
          </div>
        </div>
      </DialogBox>

      <DialogBox
        isOpen={statusDialogTenant !== null}
        onClose={closeStatusDialog}
        title={statusTargetActive ? 'Activate Tenant' : 'Deactivate Tenant'}
        panelClassName="w-[min(100vw-2rem,28rem)]"
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-700">
            {statusTargetActive ? (
              <>
                Activate tenant <span className="font-semibold">{statusDialogTenant?.tenantName}</span>?
                All users under this tenant will be set active; they must log in again.
              </>
            ) : (
              <>
                Deactivate tenant <span className="font-semibold">{statusDialogTenant?.tenantName}</span>?
                All users will be logged out immediately and cannot sign in until reactivated.
              </>
            )}
          </p>
          <div>
            <label htmlFor="tenant-status-admin-password" className="block text-sm font-medium text-slate-700 mb-1">
              Admin password
            </label>
            <input
              id="tenant-status-admin-password"
              type="password"
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setAdminPasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void confirmTenantStatusChange();
                }
              }}
              autoFocus
              autoComplete="current-password"
              disabled={submittingStatus}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your admin login password"
            />
            {adminPasswordError ? (
              <p className="text-xs text-red-600 mt-1">{adminPasswordError}</p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={closeStatusDialog}
              disabled={submittingStatus}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmTenantStatusChange()}
              isProcessing={submittingStatus}
              className={`text-white px-4 py-2 rounded-md ${
                statusTargetActive
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {statusTargetActive ? 'Activate' : 'Deactivate'}
            </Button>
          </div>
        </div>
      </DialogBox>

      <ResetTenantDataDialog
        isOpen={resetTenant !== null}
        tenantId={resetTenant?.id ?? null}
        tenantName={resetTenant?.tenantName ?? ''}
        onClose={() => setResetTenant(null)}
        onSuccess={() => {
          void fetchTenants();
        }}
      />

      {/* User List Dialog */}
      {userListTenantId && (
        <TenantUserListDialog
          isOpen={userListOpen}
          tenantId={userListTenantId}
          tenantName={tenants.find(t => t.id === userListTenantId)?.tenantName || ''}
          onClose={() => {
            setUserListOpen(false);
            setUserListTenantId(null);
          }}
        />
      )}
    </div>
  );
};

export default Tenants;
